import React, { useState, useEffect, useRef } from "react";
import "./../../../assets/css/general/Careers/CareersPage.css";

/* ============================================================
   DATA
============================================================ */
const whyJoinCards = [
  {
    icon: "🚀",
    title: "Growth Opportunities",
    desc: "Accelerate your career with structured mentorship, learning budgets, and clear promotion paths.",
  },
  {
    icon: "🤖",
    title: "Work with AI Technology",
    desc: "Build and deploy cutting-edge AI tutoring systems used by thousands of students daily.",
  },
  {
    icon: "🎯",
    title: "Real Impact on Students",
    desc: "Your work directly shapes learning outcomes and career trajectories for the next generation.",
  },
  {
    icon: "💡",
    title: "Innovation Culture",
    desc: "Experiment freely. We ship fast, learn faster, and celebrate bold ideas that challenge convention.",
  },
  {
    icon: "🌍",
    title: "Flexible & Remote",
    desc: "Work from anywhere. Async-first culture with optional in-person retreats twice a year.",
  },
  {
    icon: "🤝",
    title: "Mentorship Ecosystem",
    desc: "Be mentored by industry leaders and mentor the next wave of tech talent simultaneously.",
  },
];

const whoCanApply = [
  {
    role: "Developers",
    icon: "⚙️",
    desc: "Engineers who want to build AI products that matter. We value craftsmanship and systems thinking.",
    points: [
      "Frontend / Backend / Full-Stack",
      "AI & ML Engineers",
      "DevOps & Cloud Architects",
      "Mobile Developers",
    ],
  },
  {
    role: "Mentors",
    icon: "🏅",
    desc: "Industry experts who are passionate about teaching and want to earn while shaping careers.",
    points: [
      "5+ years industry experience",
      "Domain experts in any tech field",
      "Strong communication skills",
      "Passion for student growth",
    ],
  },
  {
    role: "Students & Interns",
    icon: "🎓",
    desc: "Ambitious learners who want real-world exposure and a launchpad for their career.",
    points: [
      "3–6 month internship programs",
      "Paid stipends & certificates",
      "Live project experience",
      "Mentorship from day one",
    ],
  },
];

const openPositions = [
  {
    id: 1,
    role: "Senior AI Engineer",
    type: "Full-time",
    location: "Remote",
    status: "Open",
    dept: "Engineering",
  },
  {
    id: 2,
    role: "Frontend Developer",
    type: "Full-time",
    location: "Remote",
    status: "Open",
    dept: "Engineering",
  },
  {
    id: 3,
    role: "Product Designer",
    type: "Full-time",
    location: "Remote",
    status: "Open",
    dept: "Design",
  },
  {
    id: 4,
    role: "Content Strategist",
    type: "Part-time",
    location: "Remote",
    status: "Open",
    dept: "Marketing",
  },
  {
    id: 5,
    role: "DevOps Engineer",
    type: "Full-time",
    location: "Remote",
    status: "Closed",
    dept: "Engineering",
  },
  {
    id: 6,
    role: "Python Mentor",
    type: "Part-time",
    location: "Remote",
    status: "Open",
    dept: "Mentorship",
  },
  {
    id: 7,
    role: "UI/UX Intern",
    type: "Internship",
    location: "Remote",
    status: "Open",
    dept: "Design",
  },
  {
    id: 8,
    role: "Marketing Intern",
    type: "Internship",
    location: "Remote",
    status: "Open",
    dept: "Marketing",
  },
];

const hiringSteps = [
  {
    step: "01",
    title: "Apply Online",
    desc: "Submit your application and portfolio through our portal.",
    icon: "📋",
  },
  {
    step: "02",
    title: "Screening",
    desc: "A quick 20-min call with our talent team to align on fit.",
    icon: "📞",
  },
  {
    step: "03",
    title: "Interview / Demo",
    desc: "Technical or teaching demo depending on your role.",
    icon: "💻",
  },
  {
    step: "04",
    title: "Final Selection",
    desc: "Offer letter and onboarding within 5 business days.",
    icon: "🎉",
  },
];

const faqs = [
  {
    q: "Do I need to be based in a specific country?",
    a: "No! We are fully remote and hire globally. As long as you have a reliable internet connection, you're welcome to apply from anywhere.",
  },
  {
    q: "Is prior EdTech experience required?",
    a: "Not at all. We value your technical skills and passion for learning. We'll onboard you to our domain through our structured program.",
  },
  {
    q: "How long does the hiring process take?",
    a: "Typically 2–3 weeks from application to offer. We move fast and respect your time.",
  },
  {
    q: "Can students apply for full-time roles?",
    a: "Final-year students can apply for internships which can convert to full-time positions based on performance.",
  },
  {
    q: "What does the mentor compensation look like?",
    a: "Mentors earn per session, with bonuses tied to student outcomes. Top mentors can earn significantly through our platform.",
  },
];

/* ============================================================
   HOOK — Intersection Observer for fade-in
============================================================ */
function useFadeIn() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ============================================================
   COMPONENTS
============================================================ */

/* ---- Animated Section Wrapper ---- */
const FadeSection = ({ children, className = "" }) => {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={`fade-section ${visible ? "fade-in" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

/* ---- Hero ---- */
const Hero = () => (
  <section className="hero-section">
    <div className="hero-bg-grid" />
    <div className="hero-orb hero-orb-1" />
    <div className="hero-orb hero-orb-2" />

    <div className="container hero-inner">
      <div className="hero-text">
        <span className="hero-badge">We're Hiring 🎉</span>
        <h1 className="hero-heading">
          Build the Future
          <br />
          <span className="hero-heading-accent">of Learning</span> with Us
        </h1>
        <p className="hero-sub">
          Join Tech Mentor Hub — where AI meets mentorship. We're building the
          platform that makes world-class education accessible, personal, and
          career-defining for every student on earth.
        </p>
        <div className="hero-ctas">
          <a href="#positions" className="btn-primary">
            View Open Roles
          </a>
          <a href="#mentor-cta" className="btn-outline">
            Become a Mentor
          </a>
        </div>
        <div className="hero-stats">
          <div className="stat">
            <span className="stat-num">12K+</span>
            <span className="stat-label">Students</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-num">340+</span>
            <span className="stat-label">Mentors</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-num">18</span>
            <span className="stat-label">Open Roles</span>
          </div>
        </div>
      </div>

      <div className="hero-visual">
        <div className="hero-card hero-card-1">
          <span className="hc-icon">🤖</span>
          <div>
            <p className="hc-title">AI Tutor Active</p>
            <p className="hc-sub">Helping 248 students now</p>
          </div>
        </div>
        <div className="hero-card hero-card-2">
          <span className="hc-icon">🏅</span>
          <div>
            <p className="hc-title">New Mentor Joined</p>
            <p className="hc-sub">Sara — Senior ML Engineer</p>
          </div>
        </div>
        <div className="hero-card hero-card-3">
          <span className="hc-icon">📈</span>
          <div>
            <p className="hc-title">Student Placement</p>
            <p className="hc-sub">Ahmed landed at Google 🎉</p>
          </div>
        </div>
        <div className="hero-blob" />
      </div>
    </div>
  </section>
);

/* ---- Why Join Us ---- */
const WhyJoin = () => (
  <FadeSection>
    <section className="section why-section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Why Us</span>
          <h2 className="section-title">
            More than a job.
            <br />A mission.
          </h2>
          <p className="section-sub">
            Everything we do is designed to help you grow while helping students
            thrive.
          </p>
        </div>
        <div className="why-grid">
          {whyJoinCards.map((c, i) => (
            <div
              className="why-card"
              key={i}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="why-icon">{c.icon}</div>
              <h3 className="why-title">{c.title}</h3>
              <p className="why-desc">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- Who Can Apply ---- */
const WhoCanApply = () => (
  <FadeSection>
    <section className="section who-section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Open To</span>
          <h2 className="section-title">Who Can Apply?</h2>
          <p className="section-sub">
            We welcome builders, teachers, and learners at every stage.
          </p>
        </div>
        <div className="who-grid">
          {whoCanApply.map((w, i) => (
            <div className="who-card" key={i}>
              <div className="who-icon">{w.icon}</div>
              <h3 className="who-role">{w.role}</h3>
              <p className="who-desc">{w.desc}</p>
              <ul className="who-list">
                {w.points.map((p, j) => (
                  <li key={j}>
                    <span className="who-check">✓</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- Open Positions ---- */
const OpenPositions = () => {
  const [typeFilter, setTypeFilter] = useState("All");
  const [deptFilter, setDeptFilter] = useState("All");

  const types = ["All", "Full-time", "Part-time", "Internship"];
  const depts = ["All", "Engineering", "Design", "Marketing", "Mentorship"];

  const filtered = openPositions.filter(
    (p) =>
      (typeFilter === "All" || p.type === typeFilter) &&
      (deptFilter === "All" || p.dept === deptFilter),
  );

  return (
    <FadeSection>
      <section className="section positions-section" id="positions">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Opportunities</span>
            <h2 className="section-title">Open Positions</h2>
            <p className="section-sub">Find the role that fits you best.</p>
          </div>

          {/* Filters */}
          <div className="filters">
            <div className="filter-group">
              <label className="filter-label">Role Type</label>
              <div className="filter-pills">
                {types.map((t) => (
                  <button
                    key={t}
                    className={`pill ${typeFilter === t ? "pill-active" : ""}`}
                    onClick={() => setTypeFilter(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">Department</label>
              <div className="filter-pills">
                {depts.map((d) => (
                  <button
                    key={d}
                    className={`pill ${deptFilter === d ? "pill-active" : ""}`}
                    onClick={() => setDeptFilter(d)}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Cards */}
          <div className="positions-list">
            {filtered.map((p) => (
              <div
                className={`position-card ${p.status === "Closed" ? "pos-closed" : ""}`}
                key={p.id}
              >
                <div className="pos-left">
                  <span className="pos-dept">{p.dept}</span>
                  <h3 className="pos-title">{p.role}</h3>
                  <div className="pos-tags">
                    <span className="pos-tag">{p.type}</span>
                    <span className="pos-tag">🌍 {p.location}</span>
                  </div>
                </div>
                <div className="pos-right">
                  <span
                    className={`pos-status ${p.status === "Open" ? "status-open" : "status-closed"}`}
                  >
                    {p.status}
                  </span>
                  {p.status === "Open" && (
                    <button className="btn-apply">Apply →</button>
                  )}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="no-results">No positions match your filters.</div>
            )}
          </div>
        </div>
      </section>
    </FadeSection>
  );
};

/* ---- Hiring Process ---- */
const HiringProcess = () => (
  <FadeSection>
    <section className="section process-section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">How It Works</span>
          <h2 className="section-title">Our Hiring Process</h2>
          <p className="section-sub">
            Simple, transparent, and respectful of your time.
          </p>
        </div>
        <div className="process-steps">
          {hiringSteps.map((s, i) => (
            <React.Fragment key={i}>
              <div className="process-step">
                <div className="step-num">{s.step}</div>
                <div className="step-icon">{s.icon}</div>
                <h3 className="step-title">{s.title}</h3>
                <p className="step-desc">{s.desc}</p>
              </div>
              {i < hiringSteps.length - 1 && <div className="step-connector" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- Mentor CTA ---- */
const MentorCTA = () => (
  <FadeSection>
    <section className="section mentor-cta-section" id="mentor-cta">
      <div className="container">
        <div className="mentor-cta-inner">
          <div className="mentor-cta-text">
            <span
              className="section-tag"
              style={{ color: "#F9F6EE", borderColor: "rgba(249,246,238,0.3)" }}
            >
              For Experts
            </span>
            <h2 className="mentor-cta-heading">
              Turn Your Expertise
              <br />
              Into Income
            </h2>
            <p className="mentor-cta-sub">
              Join our elite mentor network. Set your own hours, teach what you
              love, and build a brand that lasts — all while changing lives.
            </p>
            <div className="mentor-benefits">
              {[
                { icon: "💰", text: "Earn per session + performance bonuses" },
                { icon: "📢", text: "Build your personal brand & audience" },
                { icon: "🌱", text: "Share knowledge, grow your network" },
              ].map((b, i) => (
                <div className="mentor-benefit" key={i}>
                  <span className="mb-icon">{b.icon}</span>
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
            <a href="#" className="btn-mentor">
              Apply as Mentor →
            </a>
          </div>
          <div className="mentor-cta-visual">
            <div className="mentor-card-stack">
              <div className="mc-card mc-card-1">
                <p className="mc-earning">PKR 85,000</p>
                <p className="mc-label">Earned this month</p>
              </div>
              <div className="mc-card mc-card-2">
                <p className="mc-stat">⭐ 4.9 Rating</p>
                <p className="mc-label">From 127 students</p>
              </div>
              <div className="mc-card mc-card-3">
                <p className="mc-stat">🎓 340 Sessions</p>
                <p className="mc-label">Completed all-time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- Mission ---- */
const Mission = () => (
  <FadeSection>
    <section className="section mission-section">
      <div className="container">
        <div className="mission-inner">
          <div className="mission-icons">
            {["🎓", "🤖", "🚀", "🌍"].map((ic, i) => (
              <div className="mission-icon-wrap" key={i}>
                {ic}
              </div>
            ))}
          </div>
          <h2 className="mission-title">Our Mission</h2>
          <p className="mission-text">
            At Tech Mentor Hub, we believe that talent is everywhere but
            opportunity is not. We're building AI-powered infrastructure for
            personalized mentorship — so every student, regardless of
            background, gets access to world-class guidance, real projects, and
            a genuine shot at a career in tech.
          </p>
          <div className="mission-tags">
            {[
              "AI-Driven Learning",
              "Human Mentorship",
              "Career Outcomes",
              "Global Access",
            ].map((t, i) => (
              <span className="mission-tag" key={i}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- Final CTA ---- */
const FinalCTA = () => (
  <FadeSection>
    <section className="section final-cta-section">
      <div className="container">
        <div className="final-cta-inner">
          <h2 className="final-cta-heading">Ready to Join Us?</h2>
          <p className="final-cta-sub">
            Thousands of students are waiting for people like you to shape their
            future.
          </p>
          <div className="final-cta-btns">
            <a href="#positions" className="btn-primary">
              Apply Now
            </a>
            <a href="mailto:hr@techmentorhub.com" className="btn-outline-dark">
              Contact HR
            </a>
          </div>
        </div>
      </div>
    </section>
  </FadeSection>
);

/* ---- FAQ ---- */
const FAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <FadeSection>
      <section className="section faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">FAQs</span>
            <h2 className="section-title">Common Questions</h2>
          </div>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <div
                className={`faq-item ${open === i ? "faq-open" : ""}`}
                key={i}
              >
                <button
                  className="faq-q"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span>{f.q}</span>
                  <span className="faq-chevron">{open === i ? "−" : "+"}</span>
                </button>
                <div className="faq-a-wrap">
                  <p className="faq-a">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </FadeSection>
  );
};

/* ============================================================
   MAIN EXPORT
============================================================ */
const Careers = () => (
  <div className="careers-root">
    <Hero />
    <WhyJoin />
    <WhoCanApply />
    <OpenPositions />
    <HiringProcess />
    <MentorCTA />
    <Mission />
    <FinalCTA />
    <FAQ />
    <footer className="careers-footer">
      <p>© 2025 Tech Mentor Hub · Built with ❤️ for learners everywhere</p>
    </footer>
  </div>
);

export default Careers;
